import { useEffect, useRef } from "react";
import type { CaseRecord, RecordFact } from "../../types";
import { formatFactDate } from "../../lib/factMeta";
import { locateQuote } from "../../lib/quoteLocate";
import { useModalDismiss } from "../../lib/useModalDismiss";

// Quote-in-context: the source record with the verbatim quote highlighted
// in situ — side panel on desktop, bottom sheet on mobile (CSS-driven).
// Location uses the SAME normalization the verifier uses (quoteLocate.ts),
// so highlight-find matches verify-find. When the quote spans a formatting
// boundary and exact location fails, we fall back to the nearest match
// with a notice — never a silent no-op.
export default function QuoteContextPanel({
  fact,
  record,
  onClose,
  onNavigateToRecord,
}: {
  fact: RecordFact;
  record?: CaseRecord;
  onClose: () => void;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const markRef = useRef<HTMLElement | null>(null);
  useModalDismiss(onClose);

  useEffect(() => {
    markRef.current?.scrollIntoView({ block: "center" });
  }, [fact.id]);

  const source = (record?.body_text?.trim() || record?.ocr_text?.trim()) ?? "";
  const match = source ? locateQuote(source, fact.verbatim_quote) : null;

  let before = source;
  let hit = "";
  let after = "";
  if (match) {
    before = source.slice(0, match.start);
    hit = source.slice(match.start, match.end);
    after = source.slice(match.end);
  }

  return (
    <div
      className="quote-context-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Quote in context"
      onMouseDown={(e) => {
        // Click on the scrim (not the panel) closes — back to the row.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="quote-context-panel">
        <div className="quote-context-head">
          <div>
            <strong>{record?.subject?.trim() || "Source record"}</strong>{" "}
            <span className="overview-muted">
              {formatFactDate(record?.record_date?.slice(0, 10) ?? null)}
            </span>
          </div>
          <div className="quote-context-actions">
            {onNavigateToRecord && record && (
              <button
                type="button"
                className="fact-source-link"
                onClick={() => {
                  onClose();
                  onNavigateToRecord(record.id);
                }}
              >
                View source record →
              </button>
            )}
            <button type="button" className="fact-source-link" onClick={onClose}>
              Close ✕
            </button>
          </div>
        </div>
        {!source && (
          <p className="overview-banner">
            This record's text isn't available here — open the source record to
            see the quote in place.
          </p>
        )}
        {source && !match && (
          <p className="overview-banner">
            Couldn't locate the exact quote in this view (it may span
            formatting the text view doesn't carry). The verified quote is:
            “{fact.verbatim_quote}”
          </p>
        )}
        {source && match && !match.exact && (
          <p className="overview-banner">
            The quote spans a formatting boundary — highlighting the nearest
            match.
          </p>
        )}
        {source && (
          <pre className="quote-context-body">
            {before}
            {hit && (
              <mark ref={(el) => (markRef.current = el)} className="quote-context-mark">
                {hit}
              </mark>
            )}
            {after}
          </pre>
        )}
      </div>
    </div>
  );
}
